/**
 * Calcule les métriques de churn de manière dynamique
 * Basé sur les cycles d'achat réels et les périodes de grâce
 */

// // Étapes du calcul :
// 1. Analyser l'historique des commandes par entité
// 2. Calculer les intervalles entre commandes
// 3. Déterminer le cycle normal (médiane)
// 4. Définir la période de grâce (2× cycle)
// 5. Identifier les entités churnées
// 6. Calculer toutes les métriques dérivées

const calculateChurnMetrics = (invoices) => {
  console.log('🔍 DEBUT DEBUG CHURN - Invoices reçues:', invoices.length)
  
  // 1. Analyser l'historique des commandes par entité
  const entityOrderHistory = {}
  
  invoices.forEach(invoice => {
    const deliveryEntityId = invoice.delivery_entity_id
    const invoiceDate = new Date(invoice.created_at)
    
    if (!entityOrderHistory[deliveryEntityId]) {
      entityOrderHistory[deliveryEntityId] = []
    }
    entityOrderHistory[deliveryEntityId].push(invoiceDate)
  })

  // Trier les commandes par date pour chaque entité
  Object.keys(entityOrderHistory).forEach(entityId => {
    entityOrderHistory[entityId].sort((a, b) => a - b)
  })

  console.log('📊 Entités trouvées:', Object.keys(entityOrderHistory).length)
  console.log('📅 Sample dates (first 3 entities):', Object.entries(entityOrderHistory).slice(0, 3).map(([id, dates]) => ({
    entityId: id,
    firstOrder: dates[0]?.toISOString()?.split('T')[0],
    lastOrder: dates[dates.length - 1]?.toISOString()?.split('T')[0],
    totalOrders: dates.length
  })))

  // 2. Calculer les intervalles entre commandes pour déterminer la fréquence normale
  const allIntervals = []
  
  Object.values(entityOrderHistory).forEach(orders => {
    if (orders.length > 1) {
      for (let i = 1; i < orders.length; i++) {
        const intervalDays = Math.floor((orders[i] - orders[i-1]) / (1000 * 60 * 60 * 24))
        if (intervalDays > 0 && intervalDays < 730) { // Exclure les intervalles aberrants (> 2 ans)
          allIntervals.push(intervalDays)
        }
      }
    }
  })

  // 3. Calculer la fréquence normale (médiane des intervalles)
  let normalCycle = 90 // Défaut: 3 mois
  
  if (allIntervals.length > 0) {
    allIntervals.sort((a, b) => a - b)
    const median = allIntervals[Math.floor(allIntervals.length / 2)]
    normalCycle = median
  }

  // 4. Définir la période de grâce (2x le cycle normal, minimum 60 jours, maximum 365 jours)
  const gracePeriodDays = Math.max(60, Math.min(365, normalCycle * 2))
  
  // 5. Date de référence et seuil de churn
  const currentDate = new Date()
  const churnThresholdDate = new Date(currentDate.getTime() - (gracePeriodDays * 24 * 60 * 60 * 1000))

  console.log('⏰ Calculs temporels:')
  console.log('  - Intervalles analysés:', allIntervals.length)
  console.log('  - Cycle normal calculé:', normalCycle, 'jours')
  console.log('  - Période de grâce:', gracePeriodDays, 'jours')
  console.log('  - Date actuelle:', currentDate.toISOString().split('T')[0])
  console.log('  - Seuil de churn:', churnThresholdDate.toISOString().split('T')[0])

  // 6. Analyser chaque entité
  const entityMetrics = {}
  let debugSample = []
  
  Object.keys(entityOrderHistory).forEach(entityId => {
    const orders = entityOrderHistory[entityId]
    const firstOrder = orders[0]
    const lastOrder = orders[orders.length - 1]
    const totalOrders = orders.length

    // Client actif = au moins 1 commande
    const isOnboarded = totalOrders > 0
    
    // Client récurrent = plus d'une commande
    const hasRecurringOrders = totalOrders > 1
    
    // Churn = client actif MAIS pas de commande depuis la période de grâce
    const isChurned = isOnboarded && lastOrder < churnThresholdDate
    
    // Calculer les intervalles moyens pour cette entité
    let avgIntervalDays = normalCycle
    if (orders.length > 1) {
      const entityIntervals = []
      for (let i = 1; i < orders.length; i++) {
        entityIntervals.push(Math.floor((orders[i] - orders[i-1]) / (1000 * 60 * 60 * 24)))
      }
      avgIntervalDays = entityIntervals.reduce((sum, interval) => sum + interval, 0) / entityIntervals.length
    }

    // Calculer les jours depuis la dernière et première commande
    const daysSinceLastOrder = Math.floor((currentDate - lastOrder) / (1000 * 60 * 60 * 24))
    const daysSinceFirstOrder = Math.floor((currentDate - firstOrder) / (1000 * 60 * 60 * 24))

    entityMetrics[entityId] = {
      entityId,
      isOnboarded,
      hasRecurringOrders,
      isChurned,
      totalOrders,
      recurringOrders: Math.max(0, totalOrders - 1),
      firstOrderDate: firstOrder,
      lastOrderDate: lastOrder,
      daysSinceLastOrder,
      daysSinceFirstOrder,
      avgIntervalDays,
      orderHistory: orders
    }

    // Collecter un échantillon pour debug
    if (debugSample.length < 5) {
      debugSample.push({
        entityId: entityId.slice(0, 8),
        totalOrders,
        lastOrder: lastOrder.toISOString().split('T')[0],
        daysSinceLastOrder,
        isChurned,
        hasRecurringOrders
      })
    }
  })

  console.log('🔍 Sample entités (first 5):', debugSample)

  // 7. Calculer les statistiques globales
  const allEntities = Object.values(entityMetrics)
  const onboardedBranches = allEntities.filter(e => e.isOnboarded).length
  const recurringBranches = allEntities.filter(e => e.hasRecurringOrders && !e.isChurned).length
  const churnedBranches = allEntities.filter(e => e.isChurned).length
  
  // Calcul du churn rate = branches churned / branches onboarded
  const churnRate = onboardedBranches > 0 ? (churnedBranches / onboardedBranches) * 100 : 0
  
  // Retention = 1 - Churn (formule simple et correcte)
  const retentionRatio = 100 - churnRate

  console.log('🔥 DEBUG RETENTION DETAILED:')
  console.log('  - churnRate RAW:', churnRate)
  console.log('  - retentionRatio RAW:', retentionRatio)
  console.log('  - retentionRatio ROUNDED:', Math.round(retentionRatio * 100) / 100)

  console.log('📈 RESULTATS CALCULS:')
  console.log('  - Onboarded branches:', onboardedBranches)
  console.log('  - Recurring branches:', recurringBranches) 
  console.log('  - Churned branches:', churnedBranches)
  console.log('  - Churn rate:', churnRate.toFixed(2) + '%')
  console.log('  - Retention ratio:', retentionRatio.toFixed(2) + '%')

  const globalMetrics = {
    onboardedBranches,
    recurringBranches,
    churnedBranches,
    activeBranches: onboardedBranches - churnedBranches,
    retentionRatio: Math.round(retentionRatio * 100) / 100,
    churnRate: Math.round(churnRate * 100) / 100,
    // Métriques sur les cycles
    normalCycleDays: Math.round(normalCycle),
    gracePeriodDays,
    churnThresholdDate,
    // Statistiques sur les intervalles
    totalIntervals: allIntervals.length,
    medianIntervalDays: allIntervals.length > 0 ? allIntervals[Math.floor(allIntervals.length / 2)] : 0
  }

  return {
    entityMetrics,
    globalMetrics
  }
}

export default calculateChurnMetrics 